import { useEffect, useState } from "react";
import { Palette, Save } from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { PermissionGate } from "@/components/rbac/PermissionGate";
import { ReadOnlyScope } from "@/components/rbac/ReadOnlyScope";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { useBranding } from "@/contexts/BrandingContext";
import { Branding, BrandingPatch, updateBranding } from "@/services/branding";
import { ColorField } from "./ColorField";
import { LogoUploader } from "./LogoUploader";
import { OnboardingImagesCard } from "./OnboardingImagesCard";

const COLOR_FIELDS: Array<{
  key: keyof Branding;
  label: string;
  description?: string;
}> = [
  {
    key: "primary",
    label: "Primary",
    description: "Main brand color — buttons, links, highlights.",
  },
  {
    key: "secondary",
    label: "Secondary",
    description: "Accents and secondary buttons.",
  },
  {
    key: "accent",
    label: "Accent",
    description: "Highlights, badges, callouts.",
  },
  {
    key: "bgLight",
    label: "Background (light)",
    description: "Page background in light mode.",
  },
  {
    key: "bgDark",
    label: "Background (dark)",
    description: "Page background in dark mode.",
  },
  {
    key: "textLight",
    label: "Text (light)",
    description: "Primary text color in light mode.",
  },
  {
    key: "textDark",
    label: "Text (dark)",
    description: "Primary text color in dark mode.",
  },
  {
    key: "cardLight",
    label: "Card (light)",
    description: "Card / panel surface in light mode.",
  },
  {
    key: "cardDark",
    label: "Card (dark)",
    description: "Card / panel surface in dark mode.",
  },
  {
    key: "sidebarLight",
    label: "Sidebar (light)",
    description: "Sidebar background in light mode.",
  },
  {
    key: "sidebarDark",
    label: "Sidebar (dark)",
    description: "Sidebar background in dark mode.",
  },
];

export const BrandingPage = () => {
  const { branding, loading, apply, refresh } = useBranding();
  const [draft, setDraft] = useState<Branding | null>(branding);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (branding) setDraft(branding);
  }, [branding]);

  useEffect(() => {
    if (draft) apply(draft);
  }, [draft, apply]);

  const onSave = async () => {
    if (!draft) return;
    try {
      setSaving(true);
      const patch: BrandingPatch = {
        appName: draft.appName,
        logoUrl: draft.logoUrl,
        logoDarkUrl: draft.logoDarkUrl ?? undefined,
        faviconUrl: draft.faviconUrl ?? undefined,
        primary: draft.primary,
        secondary: draft.secondary,
        accent: draft.accent,
        bgLight: draft.bgLight,
        bgDark: draft.bgDark,
        textLight: draft.textLight,
        textDark: draft.textDark,
        cardLight: draft.cardLight,
        cardDark: draft.cardDark,
        sidebarLight: draft.sidebarLight,
        sidebarDark: draft.sidebarDark,
        splashImageUrl: draft.splashImageUrl,
        onboardingImage1Url: draft.onboardingImage1Url,
        onboardingImage2Url: draft.onboardingImage2Url,
        onboardingImage3Url: draft.onboardingImage3Url,
        onboardingVariant: draft.onboardingVariant,
      };
      const saved = await updateBranding(patch);
      apply(saved);
      toast.success("Branding saved");
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const onReset = async () => {
    await refresh();
    toast.info("Discarded unsaved changes");
  };

  if (loading && !draft) {
    return (
      <AppLayout title="Branding">
        <div className="space-y-4">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </AppLayout>
    );
  }

  if (!draft) {
    return (
      <AppLayout title="Branding">
        <p className="text-muted-foreground">Branding settings unavailable.</p>
      </AppLayout>
    );
  }

  const setField = <K extends keyof Branding>(key: K, value: Branding[K]) =>
    setDraft((prev) => (prev ? { ...prev, [key]: value } : prev));

  return (
    <AppLayout title="Branding">
      <ReadOnlyScope module="branding">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Palette className="h-4 w-4" />
            <span className="text-sm">
              Changes preview live. Save to publish to all apps.
            </span>
          </div>
          <div className="flex gap-2 shrink-0">
            <PermissionGate module="branding" action="edit">
              <Button variant="outline" onClick={onReset} disabled={saving}>
                Discard
              </Button>
            </PermissionGate>
            <PermissionGate module="branding" action="edit">
              <Button onClick={onSave} disabled={saving}>
                <Save className="mr-2 h-4 w-4" />
                {saving ? "Saving…" : "Save"}
              </Button>
            </PermissionGate>
          </div>
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Identity</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="appName">App name</Label>
                <Input
                  id="appName"
                  value={draft.appName}
                  onChange={(e) => setField("appName", e.target.value)}
                />
              </div>
              <Separator />
              <LogoUploader
                label="Logo"
                variant="light"
                currentUrl={draft.logoUrl}
                onUploaded={(url) => setField("logoUrl", url)}
              />
              <LogoUploader
                label="Logo (dark mode)"
                variant="dark"
                currentUrl={draft.logoDarkUrl}
                onUploaded={(url) => setField("logoDarkUrl", url)}
              />
              <LogoUploader
                label="Favicon"
                variant="favicon"
                currentUrl={draft.faviconUrl}
                onUploaded={(url) => setField("faviconUrl", url)}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Colors</CardTitle>
            </CardHeader>
            <CardContent>
              {COLOR_FIELDS.map((f) => (
                <ColorField
                  key={f.key}
                  label={f.label}
                  description={f.description}
                  value={draft[f.key] as string}
                  onChange={(hsl) =>
                    setField(f.key, hsl as Branding[typeof f.key])
                  }
                />
              ))}
            </CardContent>
          </Card>

          <OnboardingImagesCard
            draft={draft}
            onChange={setField}
            onVariantChange={(v) => setField("onboardingVariant", v)}
          />
        </div>
      </ReadOnlyScope>
    </AppLayout>
  );
};

export default BrandingPage;
